import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  Flex,
  Spinner,
  Text,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  Grid,
  GridItem,
  FormErrorMessage,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines, faSearch, faEdit } from "@fortawesome/free-solid-svg-icons";
import DataTable from "../AppDataTable";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import * as IconButton from "../../themes/IconButtonSet";
import { tableCustomStyles, StatusActive } from "../../themes/TableStyles";
import { StandardHeader } from "../../themes/StandardHeader";

const WarehouseManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId, profile } = useAuth();
  const [warehouses, setWarehouses] = useState([]);
  const [filteredWarehouses, setFilteredWarehouses] = useState([]);
  const [warehouseTypes, setWarehouseTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    WarehouseID: "",
    WarehouseName: "",
    WarehouseType: "",
    AddressNo: "",
    AddressMoo: "",
    AddressRoad: "",
    AddressSubdistrict: "",
    AddressDistrict: "",
    Province: "",
    PostalCode: "",
    Latitude: "",
    Longitude: "",
    Remark: "",
    Status: "1",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});

  // Check if user has permission to edit/add (OfficeType === 'สทช.')
  const hasEditPermission = profile?.OfficeType === "สทช.";

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  const columns = [
    {
      name: "รหัสคลัง",
      selector: (row) => row.WarehouseID,
      sortable: true,
      minWidth: "120px",
    },
    {
      name: "ชื่อคลัง",
      selector: (row) => row.WarehouseName,
      sortable: true,
      minWidth: "200px",
      grow: 2,
      wrap: true,
    },
    {
      name: "ประเภทคลัง",
      selector: (row) => {
        const type = warehouseTypes.find((t) => t.TypeID === row.WarehouseType);
        return type ? type.TypeName : row.WarehouseType || "-";
      },
      sortable: true,
      minWidth: "160px",
      wrap: true,
      hide: 1024,
    },
    {
      name: "สถานะ",
      selector: (row) => <StatusActive status={row.Status} />,
      sortable: true,
      minWidth: "110px",
    },
    {
      name: "จัดการ",
      cell: (row) => (
        <IconButton.Edit
          icon={faEdit}
          onClick={() => handleEdit(row)}
          label={hasEditPermission ? "แก้ไข" : "รายละเอียด"}
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      minWidth: "120px",
    },
  ];

  const fetchWarehouseTypes = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineWarehouseTypeList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setWarehouseTypes(response.TypeList || []);
      }
    } catch (error) {
      console.error("Error fetching warehouse types:", error);
    }
  };

  const fetchWarehouses = async () => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineWarehousesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const data = response.VaccineWarehousesList || [];
        setWarehouses(data);
        setFilteredWarehouses(data);
      }
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchWarehouses();
      fetchWarehouseTypes();
    }
  }, [sessionId]);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    applyFilters(value, filterType, filterStatus);
  };

  const handleTypeFilter = (e) => {
    const value = e.target.value;
    setFilterType(value);
    applyFilters(searchTerm, value, filterStatus);
  };

  const handleStatusFilter = (e) => {
    const value = e.target.value;
    setFilterStatus(value);
    applyFilters(searchTerm, filterType, value);
  };

  const applyFilters = (search, type, status) => {
    let filtered = warehouses;

    if (search) {
      const searchTerms = search.split(" ").filter((term) => term.length > 0);
      filtered = filtered.filter((row) => {
        const typeName =
          warehouseTypes.find((t) => t.TypeID === row.WarehouseType)?.TypeName ||
          row.WarehouseType ||
          "";
        return searchTerms.every((term) =>
          [
            row.WarehouseID.toLowerCase(),
            row.WarehouseName.toLowerCase(),
            typeName.toLowerCase(),
            row.Status ? "active" : "inactive",
          ].some((field) => field.includes(term))
        );
      });
    }

    if (type) {
      filtered = filtered.filter((row) => row.WarehouseType === type);
    }

    if (status) {
      filtered = filtered.filter((row) =>
        status === "1" ? row.Status : !row.Status
      );
    }

    setFilteredWarehouses(filtered);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setFilterType("");
    setFilterStatus("");
    setFilteredWarehouses(warehouses);
  };

  const handleAddNew = () => {
    setFormData({
      WarehouseID: "",
      WarehouseName: "",
      WarehouseType: "",
      AddressNo: "",
      AddressMoo: "",
      AddressRoad: "",
      AddressSubdistrict: "",
      AddressDistrict: "",
      Province: "",
      PostalCode: "",
      Latitude: "",
      Longitude: "",
      Remark: "",
      Status: "1",
    });
    setIsEditing(false);
    setShowForm(true);
    setErrors({});
  };

  const handleEdit = (row) => {
    setFormData({
      ...row,
      Latitude: row.Latitude ? row.Latitude.toString() : "",
      Longitude: row.Longitude ? row.Longitude.toString() : "",
      Status: row.Status ? "1" : "0",
    });
    setIsEditing(true);
    setShowForm(true);
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  const validateField = (name, value) => {
    let newErrors = { ...errors };
    switch (name) {
      case "WarehouseID":
      case "WarehouseName":
      case "WarehouseType":
        if (!value) {
          newErrors[name] = "กรุณากรอกข้อมูล";
        } else {
          delete newErrors[name];
        }
        break;
      case "PostalCode":
        if (value && !/^\d{5}$/.test(value)) {
          newErrors[name] = "รหัสไปรษณีย์ต้องเป็นตัวเลข 5 หลัก";
        } else {
          delete newErrors[name];
        }
        break;
      case "Latitude":
        if (value && (isNaN(value) || value < -90 || value > 90)) {
          newErrors[name] = "ละติจูดต้องเป็นตัวเลขระหว่าง -90 ถึง 90";
        } else {
          delete newErrors[name];
        }
        break;
      case "Longitude":
        if (value && (isNaN(value) || value < -180 || value > 180)) {
          newErrors[name] = "ลองจิจูดต้องเป็นตัวเลขระหว่าง -180 ถึง 180";
        } else {
          delete newErrors[name];
        }
        break;
      default:
        break;
    }
    setErrors(newErrors);
  };

  const isFormValid = () => {
    return (
      formData.WarehouseID &&
      formData.WarehouseName &&
      formData.WarehouseType &&
      (!formData.PostalCode || /^\d{5}$/.test(formData.PostalCode)) &&
      (!formData.Latitude ||
        (!isNaN(formData.Latitude) &&
          formData.Latitude >= -90 &&
          formData.Latitude <= 90)) &&
      (!formData.Longitude ||
        (!isNaN(formData.Longitude) &&
          formData.Longitude >= -180 &&
          formData.Longitude <= 180)) &&
      Object.keys(errors).length === 0
    );
  };

  const handleSave = async () => {
    if (!hasEditPermission) {
      alert("คุณไม่มีสิทธิ์ในการบันทึกข้อมูล");
      return;
    }
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน");
      return;
    }
    setIsLoading(true);
    try {
      const apiUrl = isEditing
        ? "/updateVaccineWarehousesByID"
        : "/createVaccineWarehouses";
      const response = await apiService({
        method: "POST",
        url: apiUrl,
        data: {
          ...formData,
          Latitude: formData.Latitude ? parseFloat(formData.Latitude) : null,
          Longitude: formData.Longitude ? parseFloat(formData.Longitude) : null,
          Status: formData.Status === "1",
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        alert(isEditing ? "อัปเดตคลังสำเร็จ!" : "สร้างคลังสำเร็จ!");
        setShowForm(false);
        fetchWarehouses();
      }
    } catch (error) {
      console.error("Error saving warehouse:", error);
      alert("เกิดข้อผิดพลาดในการบันทึก: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormData({
      WarehouseID: "",
      WarehouseName: "",
      WarehouseType: "",
      AddressNo: "",
      AddressMoo: "",
      AddressRoad: "",
      AddressSubdistrict: "",
      AddressDistrict: "",
      Province: "",
      PostalCode: "",
      Latitude: "",
      Longitude: "",
      Remark: "",
      Status: "1",
    });
    setErrors({});
  };

  // ไม่ลบข้อมูลออกจากฐานข้อมูล เพราะรหัสคลังถูกอ้างอิงไว้ในใบเบิก ใบจอง และประวัติการเคลื่อนไหววัคซีน
  const handleToggleStatus = async () => {
    if (!hasEditPermission) {
      alert("คุณไม่มีสิทธิ์ในการปรับสถานะข้อมูล");
      return;
    }
    const nextStatus = formData.Status !== "1";
    const confirmMessage = nextStatus
      ? "ยืนยันการเปิดการใช้งานคลังวัคซีนนี้ จะกลับมาเลือกใช้ในเอกสารใหม่ได้อีกครั้ง"
      : "ยืนยันการปิดการใช้งานคลังวัคซีนนี้ จะไม่ปรากฏให้เลือกในเอกสารใหม่ แต่ข้อมูลและเอกสารเดิมที่อ้างอิงไว้จะยังอยู่ในระบบ";

    if (!window.confirm(confirmMessage)) return;

    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/updateVaccineWarehousesByID",
        data: {
          WarehouseID: formData.WarehouseID,
          Status: nextStatus,
          UpdatedBy: profile?.DLDEmployeeID || profile?.UserID || "",
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        alert(
          nextStatus
            ? "เปิดการใช้งานคลังวัคซีนสำเร็จ"
            : "ปิดการใช้งานคลังวัคซีนสำเร็จ"
        );
        setShowForm(false);
        fetchWarehouses();
      }
    } catch (error) {
      console.error("Error updating warehouse status:", error);
      alert("เกิดข้อผิดพลาดในการปรับสถานะ: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!sessionId) {
    return null;
  }

  return (
    <Box
      p={{ base: 3, sm: 4, md: 8, lg: 12 }}
      m={{ base: 2, md: 6, lg: 10 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <VStack spacing={{ base: 4, md: 6 }} align="stretch">
        <StandardHeader
          title="รายการข้อมูลคลังวัคซีน"
          bgColor="iBlue.600"
          icon={faFileLines}
        />

        {/* ส่วนตัวกรอง */}
        <Flex
          w="100%"
          gap={3}
          direction={{ base: "column", lg: "row" }}
          align={{ base: "stretch", lg: "center" }}
          justify="space-between"
        >
          <Flex gap={3} wrap="wrap" align="center" flex="1" minW={0}>
            <InputGroup w={{ base: "100%", sm: "260px" }}>
              <InputLeftElement pointerEvents="none">
                <FontAwesomeIcon icon={faSearch} color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="ค้นหาคลัง..."
                value={searchTerm}
                onChange={handleSearch}
                colorScheme="iBlue"
              />
            </InputGroup>
            <Select
              w={{ base: "100%", sm: "200px" }}
              value={filterType}
              onChange={handleTypeFilter}
              placeholder="เลือกประเภทคลัง"
              colorScheme="iBlue"
            >
              <option value="">ทั้งหมด</option>
              {warehouseTypes.map((type) => (
                <option key={type.TypeID} value={type.TypeID}>
                  {type.TypeName}
                </option>
              ))}
            </Select>
            <Select
              w={{ base: "100%", sm: "180px" }}
              value={filterStatus}
              onChange={handleStatusFilter}
              placeholder="เลือกสถานะ"
              colorScheme="iBlue"
            >
              <option value="">ทั้งหมด</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </Select>
            <IconButton.Clear
              onClick={handleClearSearch}
              label="ล้าง"
              w={{ base: "100%", sm: "auto" }}
            />
          </Flex>
          {hasEditPermission && (
            <IconButton.Add
              onClick={handleAddNew}
              label="เพิ่มข้อมูลคลัง"
              w={{ base: "100%", lg: "auto" }}
            />
          )}
        </Flex>

        {isLoading ? (
          <Flex justify="center" py={8}>
            <Spinner size="lg" />
          </Flex>
        ) : warehouses.length === 0 ? (
          <Text fontSize={{ base: "md", md: "lg" }} color="gray.500" textAlign="center" w="100%" py={8}>
            ยังไม่มีข้อมูลคลังวัคซีน
          </Text>
        ) : filteredWarehouses.length === 0 ? (
          <Text fontSize={{ base: "md", md: "lg" }} color="gray.500" textAlign="center" w="100%" py={8}>
            ไม่พบข้อมูลที่ค้นหา
          </Text>
        ) : (
          <Box w="100%" overflowX="auto">
            <DataTable
              columns={columns}
              data={filteredWarehouses}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        )}
      </VStack>

      {/* Modal สำหรับเพิ่ม/แก้ไขข้อมูลคลังวัคซีน */}
      <Modal
        isOpen={showForm}
        onClose={handleCancel}
        size={{ base: "full", md: "3xl", lg: "5xl" }}
        scrollBehavior="inside"
        isCentered
      >
        <ModalOverlay />
        <ModalContent maxH={{ base: "100vh", md: "90vh" }} my={{ base: 0, md: 4 }}>
          <ModalHeader
            bg="iBlue.600"
            color="white"
            borderTopRadius={{ base: 0, md: "md" }}
            py={{ base: 3, md: 4 }}
          >
            <Heading size={{ base: "sm", md: "md" }} fontWeight="normal" pr={8}>
              {hasEditPermission
                ? isEditing
                  ? "แก้ไขข้อมูลคลัง"
                  : "เพิ่มข้อมูลคลัง"
                : "รายละเอียดคลังวัคซีน"}
            </Heading>
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody py={{ base: 4, md: 6 }} px={{ base: 3, md: 6 }}>
            <Grid
              templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }}
              gap={{ base: 4, md: 6 }}
            >
              <GridItem>
                <FormControl
                  isRequired
                  isInvalid={!!errors.WarehouseID}
                  isReadOnly={!hasEditPermission}
                  isDisabled={isEditing && hasEditPermission}
                >
                  <FormLabel fontSize={{ base: "sm", md: "md" }}>รหัสคลัง</FormLabel>
                  <Input
                    name="WarehouseID"
                    value={formData.WarehouseID}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                  <FormErrorMessage>{errors.WarehouseID}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl
                  isRequired
                  isInvalid={!!errors.WarehouseName}
                  isReadOnly={!hasEditPermission}
                >
                  <FormLabel fontSize={{ base: "sm", md: "md" }}>ชื่อคลัง</FormLabel>
                  <Input
                    name="WarehouseName"
                    value={formData.WarehouseName}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                  <FormErrorMessage>{errors.WarehouseName}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl
                  isRequired
                  isInvalid={!!errors.WarehouseType}
                  isDisabled={!hasEditPermission}
                >
                  <FormLabel fontSize={{ base: "sm", md: "md" }}>ประเภทคลัง</FormLabel>
                  <Select
                    name="WarehouseType"
                    value={formData.WarehouseType}
                    onChange={handleInputChange}
                    placeholder="เลือกประเภท"
                    colorScheme="iBlue"
                  >
                    {warehouseTypes.map((type) => (
                      <option key={type.TypeID} value={type.TypeID}>
                        {type.TypeName}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors.WarehouseType}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel fontSize={{ base: "sm", md: "md" }}>เลขที่อยู่</FormLabel>
                  <Input
                    name="AddressNo"
                    value={formData.AddressNo}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel fontSize={{ base: "sm", md: "md" }}>หมู่ที่</FormLabel>
                  <Input
                    name="AddressMoo"
                    value={formData.AddressMoo}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel fontSize={{ base: "sm", md: "md" }}>ถนน</FormLabel>
                  <Input
                    name="AddressRoad"
                    value={formData.AddressRoad}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel fontSize={{ base: "sm", md: "md" }}>ตำบล</FormLabel>
                  <Input
                    name="AddressSubdistrict"
                    value={formData.AddressSubdistrict}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel fontSize={{ base: "sm", md: "md" }}>อำเภอ</FormLabel>
                  <Input
                    name="AddressDistrict"
                    value={formData.AddressDistrict}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel fontSize={{ base: "sm", md: "md" }}>จังหวัด</FormLabel>
                  <Input
                    name="Province"
                    value={formData.Province}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl
                  isInvalid={!!errors.PostalCode}
                  isReadOnly={!hasEditPermission}
                >
                  <FormLabel fontSize={{ base: "sm", md: "md" }}>รหัสไปรษณีย์</FormLabel>
                  <Input
                    name="PostalCode"
                    value={formData.PostalCode}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                  <FormErrorMessage>{errors.PostalCode}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl
                  isInvalid={!!errors.Latitude}
                  isReadOnly={!hasEditPermission}
                >
                  <FormLabel fontSize={{ base: "sm", md: "md" }}>ละติจูด</FormLabel>
                  <Input
                    name="Latitude"
                    value={formData.Latitude}
                    onChange={handleInputChange}
                    type="number"
                    colorScheme="iBlue"
                  />
                  <FormErrorMessage>{errors.Latitude}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl
                  isInvalid={!!errors.Longitude}
                  isReadOnly={!hasEditPermission}
                >
                  <FormLabel fontSize={{ base: "sm", md: "md" }}>ลองจิจูด</FormLabel>
                  <Input
                    name="Longitude"
                    value={formData.Longitude}
                    onChange={handleInputChange}
                    type="number"
                    colorScheme="iBlue"
                  />
                  <FormErrorMessage>{errors.Longitude}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={{ base: 1, sm: 2 }}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel fontSize={{ base: "sm", md: "md" }}>หมายเหตุ</FormLabel>
                  <Textarea
                    name="Remark"
                    value={formData.Remark}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel fontSize={{ base: "sm", md: "md" }}>สถานะการใช้งาน</FormLabel>
                  <Flex align="center" gap={3} minH="40px">
                    <StatusActive status={formData.Status === "1"} />
                    <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500">
                      {isEditing
                        ? "ปรับได้จากปุ่มปิด/เปิดการใช้งานด้านล่าง"
                        : "ข้อมูลที่สร้างใหม่จะเปิดการใช้งานทันที"}
                    </Text>
                  </Flex>
                </FormControl>
              </GridItem>
            </Grid>
          </ModalBody>
          <ModalFooter gap={2} flexWrap="wrap" justifyContent="flex-end">
            <IconButton.Close
              onClick={handleCancel}
              label="ปิด"
              flex={{ base: "1 1 100%", sm: "0 0 auto" }}
            />
            {hasEditPermission &&
              isEditing &&
              (formData.Status === "1" ? (
                <IconButton.Cancel
                  onClick={handleToggleStatus}
                  label="ปิดการใช้งาน"
                  flex={{ base: "1 1 100%", sm: "0 0 auto" }}
                />
              ) : (
                <IconButton.Approved
                  onClick={handleToggleStatus}
                  label="เปิดการใช้งาน"
                  flex={{ base: "1 1 100%", sm: "0 0 auto" }}
                />
              ))}
            {hasEditPermission && (
              <IconButton.Save
                onClick={handleSave}
                isLoading={isLoading}
                flex={{ base: "1 1 100%", sm: "0 0 auto" }}
              />
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default WarehouseManagement;